import { compact } from './utils/array-tools.ts'
import { type Dayjs, dayjs } from './utils/dayjs.ts'
import { isEmpty } from './utils/is-empty.ts'

import type { ICancellationPolicy } from './types/cancellation-policy.ts'

function nextDay(date: string) {
  return dayjs(date).add(1, 'day').utc().format()
}

function isUTCDate(date: string) {
  return /\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])T(?:[01]\d|2[0-3])(?::[0-5]\d){2}(?:\.\d+|)(?:Z|[+-]\d{2}:?\d{2})/u.test(
    date,
  )
}

function filterMatchingCancellationPolicies({
  cancellationPolicies,
  date,
}: {
  cancellationPolicies: ICancellationPolicy[]
  date: string
}) {
  return cancellationPolicies.filter(cp =>
    dayjs(date).isBetween(cp.dateFrom, cp.dateTo, undefined, '[)'),
  )
}

export interface SanitizeCancellationPolicies {
  bookingDatetime?: string
  cancellationPolicies?: ICancellationPolicy[]
  checkInDate?: string
  releaseDate?: string
  timezone?: string
}

// eslint-disable-next-line complexity, max-statements, max-lines-per-function
export function sanitizeCancellationPolicies({
  bookingDatetime,
  cancellationPolicies,
  checkInDate,
  releaseDate,
  timezone,
}: SanitizeCancellationPolicies): ICancellationPolicy[] {
  if (!bookingDatetime) {
    throw new Error('Missing "bookingDatetime" option')
  }
  if (!checkInDate) {
    throw new Error('Missing "checkInDate" option')
  }
  if (!timezone) {
    throw new Error('Missing "timezone" option')
  }

  const bookingDatetimeUTC = dayjs(bookingDatetime).utc().format()

  const checkInDatetimeUTC = dayjs
    .tz(checkInDate, timezone)
    .startOf('day')
    .utc()
    .format()

  const nextDayCheckInDateUTC = nextDay(checkInDatetimeUTC)

  const releaseDatetimeUTC =
    releaseDate && dayjs.tz(releaseDate, timezone).startOf('day').utc().format()

  if (dayjs(bookingDatetimeUTC).isAfter(nextDayCheckInDateUTC)) {
    return sanitizeCancellationPolicies({
      bookingDatetime: checkInDatetimeUTC,
      cancellationPolicies,
      checkInDate,
      releaseDate,
      timezone,
    })
  }

  if (isEmpty(cancellationPolicies)) {
    if (dayjs(bookingDatetimeUTC).isBefore(checkInDatetimeUTC)) {
      if (releaseDatetimeUTC) {
        // eslint-disable-next-line max-depth
        if (dayjs(releaseDatetimeUTC).isBefore(bookingDatetimeUTC)) {
          return <ICancellationPolicy[]>[
            {
              dateFrom: bookingDatetimeUTC,
              dateTo: nextDayCheckInDateUTC,
              penaltyPercentage: 100,
            },
          ]
        }

        return <ICancellationPolicy[]>[
          {
            dateFrom: bookingDatetimeUTC,
            dateTo: releaseDatetimeUTC,
            penaltyPercentage: 0,
          },
          {
            dateFrom: releaseDatetimeUTC,
            dateTo: nextDayCheckInDateUTC,
            penaltyPercentage: 100,
          },
        ]
      }

      return <ICancellationPolicy[]>[
        {
          dateFrom: bookingDatetimeUTC,
          dateTo: checkInDatetimeUTC,
          penaltyPercentage: 0,
        },
        {
          dateFrom: checkInDatetimeUTC,
          dateTo: nextDayCheckInDateUTC,
          penaltyPercentage: 100,
        },
      ]
    }

    return <ICancellationPolicy[]>[
      {
        dateFrom: bookingDatetimeUTC,
        dateTo: nextDayCheckInDateUTC,
        penaltyPercentage: 100,
      },
    ]
  }

  let compactedCancellationPolicies = compact(
    cancellationPolicies?.map(cp => ({ ...cp })),
  )

  function isValidCancellationPolicy(cp: ICancellationPolicy) {
    return cp.dateFrom && cp.dateTo && dayjs(cp.dateFrom).isBefore(cp.dateTo)
  }

  const isValidCancellationPolicies = compactedCancellationPolicies.every(
    isValidCancellationPolicy,
  )

  if (!isValidCancellationPolicies) {
    return <ICancellationPolicy[]>[
      {
        dateFrom: bookingDatetimeUTC,
        dateTo: nextDayCheckInDateUTC,
        penaltyPercentage: 100,
      },
    ]
  }

  compactedCancellationPolicies = compactedCancellationPolicies
    .filter(cp => dayjs(cp.dateTo).diff(dayjs(cp.dateFrom), 'minutes') >= 20)
    .sort((a, b) => (dayjs(a.dateFrom).isAfter(dayjs(b.dateFrom)) ? 1 : -1))
    .map(cp => {
      cp.dateFrom = dayjs
        .tz(cp.dateFrom, timezone)
        .utc(isUTCDate(cp.dateFrom))
        .utc()
        .format()

      cp.dateTo = dayjs
        .tz(cp.dateTo, timezone)
        .utc(isUTCDate(cp.dateTo))
        .utc()
        .format()

      return cp
    })

  if (
    releaseDatetimeUTC &&
    dayjs(releaseDatetimeUTC).isBetween(
      dayjs(bookingDatetimeUTC),
      dayjs(compactedCancellationPolicies[0].dateFrom),
      undefined,
      '[)',
    )
  ) {
    compactedCancellationPolicies.push(
      {
        dateFrom: bookingDatetimeUTC,
        dateTo: releaseDatetimeUTC,
        penaltyPercentage: 0,
      },
      {
        dateFrom: releaseDatetimeUTC,
        dateTo: compactedCancellationPolicies[0].dateFrom,
        penaltyPercentage: 100,
      },
    )
  }

  const relevantDates = compactedCancellationPolicies
    .reduce((result: string[], cp: ICancellationPolicy) => {
      result.push(
        dayjs(cp.dateFrom).utc().format(),
        dayjs(cp.dateTo).utc().format(),
      )

      return result
    }, [])

    .concat(bookingDatetimeUTC, nextDayCheckInDateUTC)

  const sortedDates = [...new Set(relevantDates)].sort((a, b) =>
    dayjs(a).isAfter(dayjs(b)) ? 1 : -1,
  )

  let sanitizedCancellationPolicies: ICancellationPolicy[] = []

  let lastPenaltyPercentage = 0
  let lastDateFrom = ''

  for (const [index, date] of sortedDates.entries()) {
    const matchingCancellationPolicies = filterMatchingCancellationPolicies({
      cancellationPolicies: compactedCancellationPolicies,
      date,
    })

    // eslint-disable-next-line no-useless-assignment
    let penaltyPercentage: number | null = null

    if (!isEmpty(matchingCancellationPolicies)) {
      penaltyPercentage = Math.max(
        ...matchingCancellationPolicies.map(cp => cp.penaltyPercentage),
      )
    } else if (
      releaseDatetimeUTC &&
      dayjs(releaseDatetimeUTC).isBefore(dayjs(bookingDatetimeUTC))
    ) {
      penaltyPercentage = 100
    } else {
      penaltyPercentage = 0
    }

    if (!lastDateFrom) {
      lastDateFrom = date

      lastPenaltyPercentage = penaltyPercentage

      // eslint-disable-next-line no-continue
      continue
    }

    if (isEmpty(matchingCancellationPolicies)) {
      penaltyPercentage = 100
    }

    if (
      lastPenaltyPercentage !== penaltyPercentage ||
      index === sortedDates.length - 1
    ) {
      sanitizedCancellationPolicies.push({
        dateFrom: lastDateFrom,
        dateTo: date,
        penaltyPercentage: lastPenaltyPercentage,
      })

      lastDateFrom = date

      lastPenaltyPercentage = penaltyPercentage
    }
  }

  sanitizedCancellationPolicies = sanitizedCancellationPolicies.filter(
    (cp: ICancellationPolicy) =>
      !dayjs(cp.dateTo).isSameOrBefore(bookingDatetimeUTC) &&
      !dayjs(cp.dateFrom).isSameOrAfter(nextDayCheckInDateUTC),
  )

  sanitizedCancellationPolicies[0].dateFrom = bookingDatetimeUTC

  sanitizedCancellationPolicies[
    sanitizedCancellationPolicies.length - 1
  ]!.dateTo = nextDayCheckInDateUTC

  return sanitizedCancellationPolicies
}

export interface GetPenaltyPercentage {
  bookingDatetime?: string
  cancellationPolicies?: ICancellationPolicy[]
  checkInDate?: string
  datetime?: string
  releaseDate?: string
  timezone?: string
}

export function getPenaltyPercentage({
  bookingDatetime,
  cancellationPolicies,
  checkInDate,
  datetime,
  releaseDate,
  timezone,
}: GetPenaltyPercentage): number {
  if (!bookingDatetime) {
    throw new Error('Missing "bookingDatetime" option')
  }
  if (!checkInDate) {
    throw new Error('Missing "checkInDate" option')
  }
  if (!datetime) {
    throw new Error('Missing "datetime" option')
  }
  if (!timezone) {
    throw new Error('Missing "timezone" option')
  }

  const datetimeUTC = dayjs(datetime).utc().format()

  const bookingDatetimeUTC = dayjs(bookingDatetime).utc().format()

  const checkInDatetimeUTC = dayjs
    .tz(checkInDate, timezone)
    .startOf('day')
    .utc()
    .format()

  const nextDayCheckInDateUTC = nextDay(checkInDatetimeUTC)

  if (dayjs(datetimeUTC).isSameOrAfter(dayjs(nextDayCheckInDateUTC))) {
    return 100
  }

  if (dayjs(datetimeUTC).isBefore(dayjs(bookingDatetimeUTC))) {
    return 0
  }

  const sanitizedCancellationPolicies = sanitizeCancellationPolicies({
    bookingDatetime,
    cancellationPolicies,
    checkInDate,
    releaseDate,
    timezone,
  })

  const matchingCancellationPolicies = filterMatchingCancellationPolicies({
    cancellationPolicies: sanitizedCancellationPolicies,
    date: datetime,
  })

  return (
    matchingCancellationPolicies[0] &&
    matchingCancellationPolicies[0].penaltyPercentage
  )
}

export function getCurrentPenaltyPercentage({
  bookingDatetime,
  cancellationPolicies,
  checkInDate,
  releaseDate,
  timezone,
}: {
  bookingDatetime: string
  cancellationPolicies?: ICancellationPolicy[]
  checkInDate: string
  releaseDate?: string
  timezone: string
}): number {
  const datetimeUTC = dayjs().utc().format()

  return getPenaltyPercentage({
    bookingDatetime,
    cancellationPolicies,
    checkInDate,
    datetime: datetimeUTC,
    releaseDate,
    timezone,
  })
}

export function isBetterCancellationPolicies(
  newVal: ICancellationPolicy[],
  oldVal: ICancellationPolicy[],
): boolean {
  if (isEmpty(newVal)) {
    return true
  }

  if (isEmpty(oldVal)) {
    return false
  }

  if (!newVal[0].dateTo) {
    return oldVal.every(oldCancellationPolicy => {
      const relatedNewCancellationPolicy = newVal.find(newCancellationPolicy =>
        dayjs(oldCancellationPolicy.dateTo).isBetween(
          newCancellationPolicy.dateFrom,
          newCancellationPolicy.dateTo,
          null,
          '[]',
        ),
      )

      if (!relatedNewCancellationPolicy) {
        return true
      }

      return (
        oldCancellationPolicy.penaltyPercentage! >=
        relatedNewCancellationPolicy.penaltyPercentage!
      )
    })
  }

  return oldVal.every(oldCancellationPolicy => {
    const relatedNewCancellationPolicy = newVal.find(newCancellationPolicy =>
      dayjs(oldCancellationPolicy.dateTo).isBetween(
        newCancellationPolicy.dateFrom,
        newCancellationPolicy.dateTo,
        null,
        '[]',
      ),
    )

    if (!relatedNewCancellationPolicy) {
      return true
    }

    return (
      oldCancellationPolicy.penaltyPercentage! >=
      relatedNewCancellationPolicy.penaltyPercentage!
    )
  })
}

export function parseCancellationPolicies(
  cancellationPolicies: {
    dateFrom: string
    dateTo: string
    penaltyPercentage: number
  }[],
): ICancellationPolicy[] {
  if (typeof cancellationPolicies === 'string') {
    try {
      return JSON.parse(cancellationPolicies) as ICancellationPolicy[]
    } catch {
      return []
    }
  }

  return cancellationPolicies
}

export function getSanitizedCancellationPolicies({
  cancellationPolicies,
  checkInDate,
  initialDate,
  timezone,
}: {
  checkInDate: string
  cancellationPolicies: ICancellationPolicy[]
  timezone: string
  initialDate?: string
}): ICancellationPolicy[] {
  if (!cancellationPolicies.length) {
    return []
  }

  return sanitizeCancellationPolicies({
    bookingDatetime: initialDate || dayjs().format(),
    cancellationPolicies,
    checkInDate,
    timezone,
  })
}

export function getCurrentCancellationPolicy(
  sanitizedCancellationPolicies: ICancellationPolicy[],
): ICancellationPolicy {
  return (
    sanitizedCancellationPolicies.find(policy =>
      dayjs().isBetween(dayjs(policy.dateFrom), dayjs(policy.dateTo)),
    ) || sanitizedCancellationPolicies[0]
  )
}

function adjustIfFullHour(date: string): Dayjs {
  const parsed = dayjs(date)
  if (parsed.minute() === 0) {
    return parsed.subtract(1, 'minute')
  }
  return parsed
}

export function adjustForCustomers(
  cancellationPolicies: ICancellationPolicy[],
): ICancellationPolicy[] {
  return cancellationPolicies.map(policy => ({
    ...policy,
    dateFrom: adjustIfFullHour(policy.dateFrom).format(),
    dateTo: adjustIfFullHour(policy.dateTo).format(),
  }))
}
